import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { getUserReservations } from "@/services/reservationService";
import { Reservation } from "@/types/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { CalendarClock, Users, CalendarRange, BadgeEuro } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ReservationTimeline from "@/components/reservations/ReservationTimeline";
import LinkReservationDialog from "@/components/reservations/LinkReservationDialog";
import ReservationDocuments from "@/components/reservations/ReservationDocuments";
import UserClientDocuments from "@/components/reservations/UserClientDocuments";


const UserReservations = () => {
  const { user, isLoading } = useAuth();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loadingReservations, setLoadingReservations] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReservations = async () => {
    if (!user?.email) {
      console.warn("[UserReservations] Pas d'email utilisateur disponible", user);
      return;
    }

    try {
      setLoadingReservations(true);
      console.log("[UserReservations] Chargement des réservations pour:", user.email);
      const data = await getUserReservations(user.email);
      console.log("[UserReservations] Réservations reçues:", data?.length ?? 0);
      setReservations(data);
    } catch (error: any) {
      console.error("Erreur lors du chargement des réservations:", error);
      setError(error.message || "Impossible de charger vos réservations");
    } finally {
      setLoadingReservations(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchReservations();
    }
  }, [user]);

  // Rediriger vers la page de connexion si l'utilisateur n'est pas connecté
  if (!isLoading && !user) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent('/mes-reservations')}`} replace />;
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "confirmed":
        return "Confirmée";
      case "pending":
        return "En attente";
      case "cancelled":
        return "Annulée";
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 bg-villa-sand/20 pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="mb-6">
            <h1 className="text-3xl font-playfair text-villa-blue">
              Mes réservations
            </h1>
          </div>
          
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {loadingReservations ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader className="bg-gray-100">
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-16">
              <Card className="mx-auto max-w-md">
                <CardContent className="p-8">
                  <p className="text-gray-500 mb-6">
                    Vous n'avez pas encore de réservations.
                  </p>
                  <div className="space-y-4">
                    <p className="text-sm text-gray-400">Réservation introuvable ?</p>
                    <LinkReservationDialog onSuccess={fetchReservations} />
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              {user?.email && <UserClientDocuments email={user.email} />}
              <div className="space-y-6 mt-6">

                {reservations.map((reservation) => (
                  <Card key={reservation.id} className="overflow-hidden">
                    <CardHeader className="bg-villa-sand/30 pb-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl text-villa-blue">
                            Séjour du {format(new Date(reservation.check_in), "d MMMM yyyy", { locale: fr })}
                          </CardTitle>
                          <CardDescription>
                            Réservation #{reservation.id.slice(0, 8)}
                          </CardDescription>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(reservation.status)}`}>
                          {getStatusLabel(reservation.status)}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Informations de réservation */}
                        <div className="space-y-4">
                          <div className="flex items-start space-x-3">
                            <CalendarRange className="h-5 w-5 text-villa-turquoise mt-0.5" />
                            <div>
                              <p className="font-medium">Dates</p>
                              <p>
                                Du {format(new Date(reservation.check_in), "d MMMM yyyy", { locale: fr })} au{" "}
                                {format(new Date(reservation.check_out), "d MMMM yyyy", { locale: fr })}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <Users className="h-5 w-5 text-villa-turquoise mt-0.5" />
                            <div>
                              <p className="font-medium">Voyageurs</p>
                              <p>{reservation.guests} personne{reservation.guests > 1 ? "s" : ""}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <BadgeEuro className="h-5 w-5 text-villa-turquoise mt-0.5" />
                            <div>
                              <p className="font-medium">Montant total</p>
                              <p>{reservation.total_price.toLocaleString('fr-FR')} €</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <CalendarClock className="h-5 w-5 text-villa-turquoise mt-0.5" />
                            <div>
                              <p className="font-medium">Réservé le</p>
                              <p>{format(new Date(reservation.created_at), "d MMMM yyyy", { locale: fr })}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <div className="h-5 w-5 flex items-center justify-center text-villa-turquoise mt-0.5">
                              <span className="text-xs font-bold">SRC</span>
                            </div>
                            <div>
                              <p className="font-medium">Source</p>
                              <p className="text-sm px-2 py-1 bg-gray-100 rounded inline-block">{reservation.source}</p>
                            </div>
                          </div>
                          
                          {reservation.special_requests && (
                            <>
                              <Separator className="my-4" />
                              <div>
                                <p className="font-medium mb-2">Demandes spéciales</p>
                                <p className="text-gray-600">{reservation.special_requests}</p>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Timeline des étapes */}
                        <div>
                          <ReservationTimeline reservation={reservation} />
                        </div>
                      </div>
                      
                      <ReservationDocuments reservationId={reservation.id} />

                      {reservation.status === "confirmed" && (
                        <div className="mt-6 bg-green-50 p-4 rounded-md">
                          <p className="text-green-800">
                            Votre réservation est confirmée. Suivez les étapes ci-dessus pour finaliser votre séjour.
                          </p>
                        </div>
                      )}
                      
                      {reservation.status === "pending" && (
                        <div className="mt-6 bg-yellow-50 p-4 rounded-md">
                          <p className="text-yellow-800">
                            Votre réservation est en attente de confirmation. Nous vous contacterons dans les plus brefs délais.
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              {/* Section pour lier une nouvelle réservation */}
              <div className="mt-12 text-center py-8 border-t border-gray-200">
                <p className="text-gray-500 mb-4">Réservation introuvable ?</p>
                <LinkReservationDialog onSuccess={fetchReservations} />
              </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UserReservations;
